import type { Infer } from 'superstruct';
import {
	array,
	boolean,
	coerce,
	defaulted,
	nullable,
	number,
	object,
	omit,
	optional,
	partial,
	size,
	string,
} from 'superstruct';

import { ViewMediaDto } from '@adflow/shared/data-media';
import {
	parsableFloat,
	parsableInteger,
	stringDate,
	stringMaxLength,
	userString,
} from '@adflow/shared/util-superstruct';

import {
	AddGoogleAdsAdTemplateDto,
	ViewGoogleAdsAdTemplateDto,
} from './google-ads-ad.dto';
import {
	AddGoogleAdsSitelinkDto,
	ViewGoogleAdsSitelinkDto,
} from './google-ads-sitelink.dto';
import { ViewPromotionAssetDto } from './promotion-asset.dto';
import {
	AddStructuredSnippetDto,
	ViewStructuredSnippetDto,
} from './structured-snippet.dto';

export const googleAdsChannelsOptions = [
	'youtube-instream',
	'youtube-infeed',
	'youtube-shorts',
	'discover',
	'gmail',
	'maps',
];

const OptionalCalloutAssetDto = coerce(
	stringMaxLength(25),
	optional(nullable(string())),
	(value) => value ?? ''
);

export const ViewGoogleAdsTopicDto = object({
	id: number(),
	parentId: number(),
	category: string(),
});

export const ViewGoogleAdsAudienceDto = object({
	id: number(),
	category: string(),
	taxonomyType: string(),
});

export const ViewGoogleAdsUserListDto = object({
	name: string(),
	type: string(),
});

export const ViewGoogleAdsKeywordDto = object({
	id: number(),
	name: string(),
	type: string(),
	list: array(string()),

	createdAt: stringDate(),
	updatedAt: stringDate(),
});
export type ViewGoogleAdsKeywordDto = Infer<typeof ViewGoogleAdsKeywordDto>;

export const ViewGoogleAdsGroupTemplateDto = object({
	id: number(),
	name: string(),
	status: string(),
	maxCPC: parsableFloat(),
	label: optional(nullable(string())),
	aiMaxDisableSearchTermMatching: boolean(),

	channels: optional(array(string())),
	googleDisplayNetwork: optional(boolean()),

	createdAt: stringDate(),
	updatedAt: stringDate(),

	topics: array(ViewGoogleAdsTopicDto),
	audiences: array(ViewGoogleAdsAudienceDto),
	userLists: array(ViewGoogleAdsUserListDto),
	keywords: array(omit(ViewGoogleAdsKeywordDto, ['list'])),
	promotionAssets: optional(array(ViewPromotionAssetDto)),
	images: optional(array(ViewMediaDto)),
	structuredSnippets: optional(array(ViewStructuredSnippetDto)),
	sitelinks: optional(array(ViewGoogleAdsSitelinkDto)),
	calloutAssets: optional(array(string())),

	adTemplates: optional(array(ViewGoogleAdsAdTemplateDto)),
});

export const AddGoogleAdsGroupTemplateDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	status: userString('validations:Required'),
	maxCPC: parsableFloat(),
	label: optional(nullable(string())),
	aiMaxDisableSearchTermMatching: defaulted(boolean(), false),

	channels: optional(array(string())),
	googleDisplayNetwork: optional(boolean()),

	topics: optional(array(ViewGoogleAdsTopicDto)),
	audiences: optional(array(ViewGoogleAdsAudienceDto)),
	userLists: optional(array(ViewGoogleAdsUserListDto)),
	keywords: optional(array(object({ id: number() }))),
	promotionAssets: optional(array(object({ id: number() }))),
	images: defaulted(size(array(ViewMediaDto), 0, 20), []),
	structuredSnippets: optional(array(AddStructuredSnippetDto)),
	sitelinks: optional(array(AddGoogleAdsSitelinkDto)),
	calloutAssets: defaulted(size(array(OptionalCalloutAssetDto), 0, 10), []),

	adTemplates: optional(array(AddGoogleAdsAdTemplateDto)),
});

export const EditGoogleAdsGroupTemplateDto = partial(
	AddGoogleAdsGroupTemplateDto
);

export type ViewGoogleAdsGroupTemplateDto = Infer<
	typeof ViewGoogleAdsGroupTemplateDto
>;
export type AddGoogleAdsGroupTemplateDto = Infer<
	typeof AddGoogleAdsGroupTemplateDto
>;
export type EditGoogleAdsGroupTemplateDto = Infer<
	typeof EditGoogleAdsGroupTemplateDto
>;
